import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginForm } from './components/LoginForm';
import { LandingPage } from './components/LandingPage';
import { Header } from './components/Header';
import { Dashboard } from './components/Dashboard';
import { PublicCampaignView } from './components/PublicCampaignView';
import { PartnerCampaignPreview } from './components/PartnerCampaignPreview';
import { TermsOfService } from './components/TermsOfService';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { ContactUs } from './components/ContactUs';
import { DebugDataView } from './components/DebugDataView';
import { PasswordChangeModal } from './components/PasswordChangeModal';
import { AdvertisementDebugView } from './components/AdvertisementDebugView';
import { ActivateAccount } from './components/ActivateAccount';
import { ConfirmSubscription } from './components/ConfirmSubscription';
import { DebugRecaptcha } from './components/DebugRecaptcha';
import { PageViewTrackingProvider } from './components/PageViewTrackingProvider';
import { DirectAdView } from './components/DirectAdView';

const DashboardContent: React.FC = () => {
  const { user, loading, needsPasswordChange, changePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
          <p className="text-xs text-gray-500 mt-2">Check console for details</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show password change modal if needed
  if (needsPasswordChange && user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DebugRecaptcha />
        <Header />
        <PasswordChangeModal
          isOpen={true}
          onPasswordChanged={changePassword}
          userEmail={user.email}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Dashboard />
    </div>
  );
};

const LoginPage: React.FC = () => {
  const { user } = useAuth();
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <LoginForm />
    </div>
  );
};

function App() {
  return (
    <Router>
      <PageViewTrackingProvider>
        <AuthProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activate-account" element={<ActivateAccount />} />
            <Route path="/campaign/:campaignId" element={<PublicCampaignView />} />
            <Route path="/ad/:adId" element={<DirectAdView />} />
            <Route path="/partner-campaign-preview" element={<PartnerCampaignPreview />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={<DashboardContent />} />
            <Route path="/debug" element={<DebugDataView />} />
            <Route path="/debug-ads" element={<AdvertisementDebugView />} />
            
            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </PageViewTrackingProvider>
    </Router>
  );
}

export default App;